import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { Location } from 'components/location/models/LocationModel';

import { ApplicationState } from 'store/store';
import { ReviewsPreviewCard } from './ReviewsPreviewCard';
import { useMediaCheck } from 'lib/hooks/useMediaCheck';
import { contentApi } from 'components/content/contentApi';
import { ContentLayout } from 'components/content/models/ContentLayout';
import { LocationSettingsApi } from 'components/location/LocationSettingsApi';

interface ReviewsPreviewProps {
    allLocations: Location[];
}

export const ReviewsPreview = ({ allLocations }: ReviewsPreviewProps) => {
    const [activeLayout, setActiveLayout] = React.useState<ContentLayout | null>(null);
    const [locationTitle, setLocationTitle] = React.useState<string>('');
    const [minimumRating, setMinimumRating] = React.useState<number>(4);
    const [googleReviewRedirectUrl, setGoogleReviewRedirectUrl] = React.useState<string>('');

    const { tenant } = useSelector((state: ApplicationState) => state.auth);

    const appIconUrl = React.useMemo(
        () => `${import.meta.env.MEDIA_SERVICE_URL}/tenants/${tenant?.tenantId}/app_assets/icon_40`,
        [tenant]
    );
    const [, , , getCustomFontUrl] = useMediaCheck(
        `${import.meta.env.MEDIA_SERVICE_URL}/tenants/${tenant?.tenantId}/app_assets/customfont`,
        'customfont'
    );
    const [, , , getSecondaryCustomFontUrl] = useMediaCheck(
        `${import.meta.env.MEDIA_SERVICE_URL}/tenants/${tenant?.tenantId}/app_assets/secondary_custom_font`,
        'customSecondaryFont'
    );

    React.useEffect(() => {
        async function load() {
            const defaultLocation = allLocations[0];
            if (defaultLocation) {
                setLocationTitle(defaultLocation.title);
                const settings = await new LocationSettingsApi().getPublicSettings(
                    null,
                    defaultLocation._id,
                    false
                );
                setMinimumRating(settings.reviews?.minimumRatingToTriggerExternalReview ?? 4);
                setGoogleReviewRedirectUrl(settings.reviews?.externalReviewUrl ?? '');
            }
        }
        if (allLocations.length > 0) {
            load();
        }
    }, [allLocations]);

    React.useEffect(() => {
        async function load() {
            const activeLayoutResult = await contentApi.layouts.getActiveLayout();
            if (activeLayoutResult.ok) {
                setActiveLayout(activeLayoutResult.body);
            }
        }
        load();
    }, []);

    return (
        <Box display="flex" flexDirection="column" gap={2} paddingBottom={6} sx={{ px: 2 }}>
            <Typography variant="body2">
                This is a preview of the reviews prompt that will be shown to customers.
            </Typography>
            <ReviewsPreviewCard
                activeLayout={activeLayout}
                locationTitle={locationTitle}
                minimumRating={minimumRating}
                googleReviewRedirectUrl={googleReviewRedirectUrl}
                appIconUrl={appIconUrl}
                getCustomFontUrl={getCustomFontUrl()}
                getSecondaryCustomFontUrl={getSecondaryCustomFontUrl()}
            />
        </Box>
    );
};
